import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { Shield, Ban, Lock, Flame, Eye, FileText, Scale, Users, Zap, Mail } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";


export default function AntiFraud() {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col relative">
      {/* Background effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--destructive)/0.15),transparent_40%),radial-gradient(circle_at_70%_80%,hsl(var(--primary)/0.1),transparent_40%)] pointer-events-none" />
      
      <AppHeader currentPage="anti-fraud" />
      
      <main className="flex-1 relative z-10 pt-20 pb-24 px-4">
        <div className="max-w-7xl mx-auto">
          <PageHero
            title="Anti-Fraud Policy"
            subtitle="How we protect our community from fraudulent activity"
            badge={
              <Badge variant="secondary" className="bg-destructive/20 backdrop-blur-sm border-destructive/30 text-destructive">
                <Shield className="w-3 h-3 mr-1" />
                Security Policy
              </Badge>
            }
          />

          {/* Content cards */}
          <div className="space-y-6">
            {/* Overview */}
            <Card className="bg-gradient-to-br from-red-500/10 via-card to-card border-red-500/20 hover:border-red-500/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl bg-destructive flex-shrink-0">
                    <Eye className="h-5 w-5 text-destructive-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Fraud Detection System</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Flow employs advanced automated systems that continuously monitor all platform activity. 
                      Our fraud detection analyzes transaction patterns, device fingerprints, IP addresses, 
                      and behavioral signals to identify and prevent abusive activity before it affects our community.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Prohibited Activities */}
            <Card className="bg-gradient-to-br from-orange-500/10 via-card to-card border-orange-500/20 hover:border-orange-500/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl bg-destructive flex-shrink-0">
                    <Ban className="h-5 w-5 text-destructive-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Prohibited Activities</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      The following activities are strictly prohibited and will result in enforcement action:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-2">
                      <li><strong className="text-foreground">Wash Trading:</strong> Buying and selling NFTs between your own wallets to inflate sales metrics</li>
                      <li><strong className="text-foreground">Self-Trading:</strong> Trading with yourself using multiple accounts or devices</li>
                      <li><strong className="text-foreground">Price Manipulation:</strong> Artificially inflating or deflating NFT prices</li>
                      <li><strong className="text-foreground">Multi-Wallet Abuse:</strong> Using multiple wallets to circumvent platform limits or detection</li>
                      <li><strong className="text-foreground">Artificial Boost:</strong> Fake engagement or artificially boosting NFT visibility</li>
                      <li><strong className="text-foreground">VPN/Tor Evasion:</strong> Using privacy tools to evade detection or sanctions</li>
                      <li><strong className="text-foreground">Cyclic Trading:</strong> Coordinated trading patterns between related accounts</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Strike System */}
            <Card className="bg-gradient-to-br from-yellow-500/10 via-card to-card border-yellow-500/20 hover:border-yellow-500/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl bg-primary flex-shrink-0">
                    <Zap className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Strike System</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Our enforcement follows a progressive strike system:
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                        <Badge className="bg-primary text-primary-foreground border-0">Strike 1</Badge>
                        <div>
                          <p className="font-medium text-foreground">Warning</p>
                          <p className="text-sm text-muted-foreground">Internal log created. No visible action.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                        <Badge className="bg-accent text-accent-foreground border-0">Strike 2</Badge>
                        <div>
                          <p className="font-medium text-foreground">Restricted</p>
                          <p className="text-sm text-muted-foreground">
                            ⚠️ "We have detected suspicious activity on your account. Any further violation will result in permanent ban and forfeiture of all frozen assets."
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                        <Badge className="bg-destructive text-destructive-foreground border-0">Strike 3</Badge>
                        <div>
                          <p className="font-medium text-foreground">Banned</p>
                          <p className="text-sm text-muted-foreground">
                            ❌ Access blocked. NFTs frozen. EMON tokens locked and marked for burn.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Asset Handling */}
            <Card id="asset-enforcement" className="bg-gradient-to-br from-red-500/20 via-card to-card border-red-500/30 hover:border-red-500/50 transition-all duration-300 backdrop-blur-sm scroll-mt-24">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl bg-destructive flex-shrink-0">
                    <Flame className="h-5 w-5 text-destructive-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Asset Enforcement</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      When enforcement action is taken, the following may occur:
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 bg-card/50 rounded-lg border border-border/50">
                        <h3 className="font-semibold text-foreground flex items-center gap-2 mb-2">
                          <Lock className="w-4 h-4 text-primary" />
                          EMON Tokens
                        </h3>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          <li>• Tokens held in escrow/PDA</li>
                          <li>• Frozen upon detection</li>
                          <li>• Subject to permanent burn</li>
                          <li>• No refunds or compensation</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-card/50 rounded-lg border border-border/50">
                        <h3 className="font-semibold text-foreground flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4 text-primary" />
                          NFTs
                        </h3>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          <li>• Removed from marketplace</li>
                          <li>• Hidden from public feed</li>
                          <li>• Cannot be traded or sold</li>
                          <li>• Metadata may be frozen</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Detection Methods */}
            <Card className="bg-gradient-to-br from-purple-500/10 via-card to-card border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                    <Users className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Identity Clustering</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Our system links related accounts through multiple signals:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1">
                      <li>Device fingerprints (hardware, browser, canvas)</li>
                      <li>IP addresses and geolocation patterns</li>
                      <li>Wallet transaction history and relationships</li>
                      <li>Behavioral patterns and timing analysis</li>
                      <li>VPN, Tor, and proxy detection</li>
                    </ul>
                    <p className="text-sm text-muted-foreground mt-4 border-l-2 border-purple-500/50 pl-4 italic">
                      Multiple wallets controlled by the same person are automatically grouped into a single "Fraud Identity" 
                      and enforcement applies to all linked accounts.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Legal Notice */}
            <Card className="bg-gradient-to-br from-slate-500/10 via-card to-card border-slate-500/20 hover:border-slate-500/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl bg-muted flex-shrink-0">
                    <Scale className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Legal Notice</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      By using Flow Marketplace, you explicitly acknowledge and agree to:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground space-y-2">
                      <li>Our right to monitor all platform activity</li>
                      <li>Automated enforcement without prior notice</li>
                      <li>Potential loss of tokens and assets due to violations</li>
                      <li>Permanent bans being irreversible</li>
                      <li>Waiving any right to compensation for enforcement actions</li>
                    </ul>
                    <div className="flex flex-wrap gap-3 mt-6">
                      <Link 
                        to="/terms" 
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/20 hover:bg-primary/30 text-primary font-medium transition-all duration-300 hover:scale-105"
                      >
                        <FileText className="h-4 w-4" />
                        Terms of Service
                      </Link>
                      <Link 
                        to="/privacy" 
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/20 hover:bg-primary/30 text-primary font-medium transition-all duration-300 hover:scale-105"
                      >
                        <Shield className="h-4 w-4" />
                        Privacy Policy
                      </Link>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="bg-gradient-to-br from-primary/20 via-accent/10 to-card border-primary/30 hover:border-primary/50 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start overflow-hidden">
                  <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                    <Mail className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-xl font-semibold text-foreground mb-2">Questions or Disputes</h2>
                    <p className="text-muted-foreground leading-relaxed mb-3">
                      If you believe an enforcement action was made in error, contact our support team:
                    </p>
                    <a 
                      href="mailto:support@emonboost.com" 
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/20 hover:bg-primary/30 text-primary font-medium transition-all duration-300 hover:scale-105 text-sm sm:text-base break-all"
                    >
                      <Mail className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate sm:overflow-visible">support@emonboost.com</span>
                    </a>
                    <p className="text-xs text-muted-foreground mt-3">
                      Note: Filing a dispute does not guarantee reversal of enforcement actions.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
